import { FileEntry, FleetNode } from '../../shared/types';
import { iconSvg } from '../components/Icons';

export interface TransferQueueItem {
  id: string;
  fileName: string;
  sourceNode: string;
  targetNode: string;
  targetDir: string;
  sizeBytes: number;
  status: 'QUEUED' | 'TRANSFERRING' | 'COMPLETED' | 'ERROR';
  base64Payload?: string;
  errorMsg?: string;
}

export class DualPaneFileManager {
  private leftNodeId = 'ubuntu-pc';
  private leftCwd = '/home/ea/Documents/Projects/ea-remote-desktop';
  private leftEntries: FileEntry[] = [];
  private rightNodeId = 'orchestrator-core';
  private rightCwd = '/opt/ea-remote-desktop';
  private rightEntries: FileEntry[] = [];

  private editorState: {
    nodeId: string;
    path: string;
    content: string;
    originalContent: string;
    mode: string;
    lang: string;
  } | null = null;

  private chmodModal: {
    nodeId: string;
    path: string;
    mode: string;
    owner: string;
    group: string;
  } | null = null;

  private queue: TransferQueueItem[] = [];

  private readonly getNodes: () => FleetNode[];
  private readonly apiFetch: <T>(url: string, options?: RequestInit) => Promise<T>;
  private readonly onStatus: (msg: string) => void;

  constructor(opts: {
    getNodes: () => FleetNode[];
    apiFetch: <T>(url: string, options?: RequestInit) => Promise<T>;
    onStatus: (msg: string) => void;
  }) {
    this.getNodes = opts.getNodes;
    this.apiFetch = opts.apiFetch;
    this.onStatus = opts.onStatus;
  }

  public setRightTargetNode(nodeId: string): void {
    this.rightNodeId = nodeId;
  }

  public async refreshBothPanes(container?: HTMLElement): Promise<void> {
    try {
      const [left, right] = await Promise.all([
        this.apiFetch<{ cwd: string; entries: FileEntry[] }>(
          `/api/v1/files/list?nodeId=${encodeURIComponent(this.leftNodeId)}&path=${encodeURIComponent(this.leftCwd)}`
        ),
        this.apiFetch<{ cwd: string; entries: FileEntry[] }>(
          `/api/v1/files/list?nodeId=${encodeURIComponent(this.rightNodeId)}&path=${encodeURIComponent(this.rightCwd)}`
        )
      ]);
      this.leftCwd = left.cwd;
      this.leftEntries = left.entries;
      this.rightCwd = right.cwd;
      this.rightEntries = right.entries;
      if (container) {
        this.render(container);
      }
    } catch (e) {
      this.onStatus(`SFTP sync warning: ${e instanceof Error ? e.message : 'error'}`);
    }
  }

  private detectLanguage(filePath: string): string {
    const ext = filePath.split('.').pop()?.toLowerCase() || '';
    const map: Record<string, string> = {
      ts: 'TypeScript',
      js: 'JavaScript',
      json: 'JSON',
      py: 'Python',
      sh: 'Bash Shell',
      service: 'Systemd Unit',
      conf: 'Configuration',
      md: 'Markdown',
      css: 'Stylesheet',
      html: 'HTML'
    };
    return map[ext] || 'PlainText';
  }

  public render(container: HTMLElement): void {
    const isDirty = this.editorState ? this.editorState.content !== this.editorState.originalContent : false;
    const lineCount = this.editorState ? Math.max(1, this.editorState.content.split('\n').length) : 1;
    const lineNums = Array.from({ length: lineCount }, (_, i) => i + 1).join('\n');

    container.innerHTML = `
      <div style="flex:1;display:flex;flex-direction:column;overflow:hidden;">
        <!-- Top Dual-Pane SFTP Explorer -->
        <div class="nx-sftp-dual">
          ${this.renderPane('LEFT', this.leftNodeId, this.leftCwd, this.leftEntries)}
          ${this.renderPane('RIGHT', this.rightNodeId, this.rightCwd, this.rightEntries)}
        </div>

        <!-- Bottom Integrated Code Editor & Resilient Transfer Queue -->
        <div style="height:230px;background:#101318;border-top:1px solid #1E2430;display:flex;overflow:hidden;">
          <!-- Syntax Code Editor with Line Gutter -->
          <div style="flex:1;display:flex;flex-direction:column;border-right:1px solid #1E2430;overflow:hidden;">
            <div style="display:flex;align-items:center;justify-content:space-between;height:30px;padding:0 10px;background:#161A22;border-bottom:1px solid #1E2430;">
              <div style="display:flex;align-items:center;gap:8px;font-size:11px;">
                ${iconSvg('file-code', 13)}
                <span style="font-weight:600;color:#F3F4F6;">
                  ${this.editorState ? `${this.escapeHtml(this.editorState.path)} [${this.escapeHtml(this.editorState.nodeId)}]` : 'INTEGRATED REMOTE CONFIGURATION EDITOR'}
                </span>
                ${this.editorState ? `<span class="nx-tag mono">${this.escapeHtml(this.editorState.lang)} | ${this.escapeHtml(this.editorState.mode)}</span>` : ''}
                ${isDirty ? `<span class="nx-tag mono" style="color:#F59E0B;border-color:#F59E0B;">MODIFIED</span>` : ''}
              </div>
              <div style="display:flex;gap:6px;">
                ${this.editorState ? `
                  <button id="nx-editor-save" class="nx-btn nx-btn-primary" style="height:22px;" type="button">${iconSvg('check', 11)} Save Remote (Ctrl+S)</button>
                ` : ''}
              </div>
            </div>
            <div style="flex:1;display:flex;background:#090B0E;overflow:hidden;">
              <pre id="nx-editor-gutter" class="mono" style="width:44px;padding:8px 6px;text-align:right;color:#6B7280;background:#101318;border-right:1px solid #1E2430;font-size:12px;line-height:1.45;overflow:hidden;user-select:none;">${lineNums}</pre>
              <textarea id="nx-editor-textarea" class="mono" style="flex:1;padding:8px 10px;background:#090B0E;color:#F3F4F6;border:none;outline:none;font-size:12px;line-height:1.45;resize:none;white-space:pre;overflow:auto;" placeholder="Select 'Edit' on any remote file to load its contents into the syntax workspace...">${this.escapeHtml(this.editorState?.content || '')}</textarea>
            </div>
          </div>

          <!-- Transfer Queue Drawer -->
          <div style="width:390px;display:flex;flex-direction:column;background:#101318;overflow:hidden;">
            <div style="display:flex;align-items:center;justify-content:space-between;height:30px;padding:0 10px;background:#161A22;border-bottom:1px solid #1E2430;font-size:11px;font-weight:600;color:#9CA3AF;">
              <span>TRANSFER QUEUE &amp; RESUME (${this.queue.length})</span>
              <button id="nx-clear-queue" class="nx-btn" style="height:20px;padding:0 6px;font-size:10px;" type="button">Clear Done</button>
            </div>
            <div style="flex:1;overflow-y:auto;padding:6px 10px;">
              ${this.queue.map(q => `
                <div style="display:flex;align-items:center;justify-content:space-between;padding:5px 0;border-bottom:1px solid #1E2430;font-family:var(--font-mono);font-size:11px;">
                  <div>
                    <div style="color:#F3F4F6;">${this.escapeHtml(q.fileName)} <span style="color:#6B7280;">(${q.sizeBytes} B)</span></div>
                    <div style="color:#6B7280;font-size:10px;">${this.escapeHtml(q.sourceNode)} -> ${this.escapeHtml(q.targetNode)}:${this.escapeHtml(q.targetDir)}</div>
                  </div>
                  <div style="display:flex;align-items:center;gap:4px;">
                    <span class="nx-tag" style="color:${q.status === 'COMPLETED' ? '#10B981' : q.status === 'ERROR' ? '#EF4444' : '#3B82F6'}">${q.status}</span>
                    ${q.status === 'ERROR' ? `<button class="nx-btn act-retry-tx" data-id="${q.id}" style="height:20px;padding:0 6px;" type="button">Retry</button>` : ''}
                  </div>
                </div>
              `).join('') || `<div class="mono" style="color:#6B7280;font-size:11px;padding-top:8px;">Drop files onto either pane or click Copy (-> / <-) to transfer between hosts.</div>`}
            </div>
          </div>
        </div>
      </div>
      ${this.chmodModal ? this.renderChmodModal() : ''}
    `;

    this.bindEvents(container);
  }

  private renderPane(side: 'LEFT' | 'RIGHT', nodeId: string, cwd: string, entries: FileEntry[]): string {
    const nodes = this.getNodes();
    const copyArrow = side === 'LEFT' ? '->' : '<-';
    return `
      <div class="nx-sftp-pane act-dropzone" data-side="${side}">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:6px 10px;background:#161A22;border-bottom:1px solid #1E2430;gap:6px;">
          <select class="nx-select mono act-sftp-node" data-side="${side}" style="width:165px;">
            ${nodes.map(n => `<option value="${this.escapeHtml(n.id)}" ${n.id === nodeId ? 'selected' : ''}>${side}: ${this.escapeHtml(n.name)}</option>`).join('')}
          </select>
          <input class="nx-input mono act-sftp-path" data-side="${side}" style="flex:1;" type="text" value="${this.escapeHtml(cwd)}" />
          <button class="nx-btn act-sftp-up" data-side="${side}" title="Parent Directory" type="button">Up</button>
          <button class="nx-btn act-sftp-mkdir" data-side="${side}" title="New Folder" type="button">${iconSvg('plus', 11)} Dir</button>
          <label class="nx-btn nx-btn-primary" style="cursor:pointer;">
            ${iconSvg('upload', 11)} Upload
            <input class="act-sftp-upload" data-side="${side}" type="file" multiple style="display:none;" />
          </label>
        </div>
        <div style="flex:1;overflow-y:auto;">
          <table class="nx-table">
            <thead>
              <tr><th>Name</th><th>Size</th><th>Mode</th><th>Owner</th><th>Operations</th></tr>
            </thead>
            <tbody>
              ${entries.map(e => `
                <tr>
                  <td class="mono" style="cursor:pointer;color:${e.isDir ? '#3B82F6' : '#F3F4F6'};font-weight:${e.isDir ? '600' : '400'};">
                    <span class="act-sftp-nav" data-side="${side}" data-path="${this.escapeHtml(e.path)}" data-isdir="${e.isDir}">
                      ${e.isDir ? '[DIR] ' : ''}${this.escapeHtml(e.name)}
                    </span>
                  </td>
                  <td class="mono" style="color:#9CA3AF;">${e.isDir ? '-' : this.formatBytes(e.size)}</td>
                  <td class="mono"><span class="nx-tag">${this.escapeHtml(e.mode)}</span></td>
                  <td class="mono" style="color:#9CA3AF;">${this.escapeHtml(e.owner)}:${this.escapeHtml(e.group)}</td>
                  <td>
                    <div style="display:flex;gap:4px;">
                      ${!e.isDir ? `
                        <button class="nx-btn act-sftp-edit" data-node="${this.escapeHtml(nodeId)}" data-path="${this.escapeHtml(e.path)}" style="height:22px;padding:0 6px;" type="button">Edit</button>
                        <button class="nx-btn act-sftp-copy" data-side="${side}" data-path="${this.escapeHtml(e.path)}" data-name="${this.escapeHtml(e.name)}" style="height:22px;padding:0 6px;" title="Copy to opposite pane" type="button">${copyArrow}</button>
                      ` : ''}
                      <button class="nx-btn act-sftp-chmod-open" data-node="${this.escapeHtml(nodeId)}" data-path="${this.escapeHtml(e.path)}" data-mode="${this.escapeHtml(e.mode)}" data-owner="${this.escapeHtml(e.owner)}" data-group="${this.escapeHtml(e.group)}" style="height:22px;padding:0 6px;" type="button">Perm</button>
                      <a class="nx-btn" href="/api/v1/files/download?nodeId=${encodeURIComponent(nodeId)}&path=${encodeURIComponent(e.path)}" style="height:22px;padding:0 6px;" download>${iconSvg('download', 11)}</a>
                      <button class="nx-btn nx-btn-danger act-sftp-del" data-node="${this.escapeHtml(nodeId)}" data-path="${this.escapeHtml(e.path)}" style="height:22px;padding:0 6px;" type="button">${iconSvg('trash', 11)}</button>
                    </div>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  private renderChmodModal(): string {
    if (!this.chmodModal) return '';
    return `
      <div class="nx-modal-backdrop">
        <div class="nx-cmd-modal" style="width:440px;padding:14px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
            <span style="font-weight:600;">PERMISSIONS &amp; OWNERSHIP (CHMOD / CHOWN)</span>
            <button id="nx-close-chmod" class="nx-btn" type="button">${iconSvg('x', 12)}</button>
          </div>
          <div style="display:flex;flex-direction:column;gap:8px;">
            <div class="mono" style="font-size:11px;color:#9CA3AF;">Target: ${this.escapeHtml(this.chmodModal.path)}</div>
            <label style="font-size:11px;color:#9CA3AF;">Octal Mode (e.g. 0644, 0755, 0600):</label>
            <input id="chmod-mode" class="nx-input mono" type="text" value="${this.escapeHtml(this.chmodModal.mode)}" />
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
              <div>
                <label style="font-size:11px;color:#9CA3AF;">Owner:</label>
                <input id="chmod-owner" class="nx-input mono" style="width:100%;" type="text" value="${this.escapeHtml(this.chmodModal.owner)}" />
              </div>
              <div>
                <label style="font-size:11px;color:#9CA3AF;">Group:</label>
                <input id="chmod-group" class="nx-input mono" style="width:100%;" type="text" value="${this.escapeHtml(this.chmodModal.group)}" />
              </div>
            </div>
            <button id="nx-apply-chmod" class="nx-btn nx-btn-primary" style="margin-top:4px;" type="button">${iconSvg('check', 12)} Apply Chmod / Chown</button>
          </div>
        </div>
      </div>
    `;
  }

  private bindEvents(container: HTMLElement): void {
    container.querySelectorAll('.act-sftp-node').forEach(sel => {
      sel.addEventListener('change', () => {
        const el = sel as HTMLSelectElement;
        if (el.dataset.side === 'LEFT') this.leftNodeId = el.value;
        else this.rightNodeId = el.value;
        void this.refreshBothPanes(container);
      });
    });

    container.querySelectorAll('.act-sftp-path').forEach(inp => {
      inp.addEventListener('keydown', e => {
        if ((e as KeyboardEvent).key === 'Enter') {
          const el = inp as HTMLInputElement;
          if (el.dataset.side === 'LEFT') this.leftCwd = el.value;
          else this.rightCwd = el.value;
          void this.refreshBothPanes(container);
        }
      });
    });

    container.querySelectorAll('.act-sftp-up').forEach(btn => {
      btn.addEventListener('click', () => {
        const side = (btn as HTMLElement).dataset.side;
        const cur = side === 'LEFT' ? this.leftCwd : this.rightCwd;
        const parent = cur.split('/').slice(0, -1).join('/') || '/';
        if (side === 'LEFT') this.leftCwd = parent;
        else this.rightCwd = parent;
        void this.refreshBothPanes(container);
      });
    });

    container.querySelectorAll('.act-sftp-mkdir').forEach(btn => {
      btn.addEventListener('click', async () => {
        const side = (btn as HTMLElement).dataset.side;
        const nodeId = side === 'LEFT' ? this.leftNodeId : this.rightNodeId;
        const cwd = side === 'LEFT' ? this.leftCwd : this.rightCwd;
        const folderPath = `${cwd.replace(/\/$/, '')}/dir-${Date.now().toString().slice(-4)}`;
        await this.apiFetch('/api/v1/files/mkdir', {
          method: 'POST',
          body: JSON.stringify({ nodeId, path: folderPath })
        });
        this.onStatus(`Created directory ${folderPath}`);
        await this.refreshBothPanes(container);
      });
    });

    container.querySelectorAll('.act-sftp-nav').forEach(el => {
      el.addEventListener('click', () => {
        const h = el as HTMLElement;
        if (h.dataset.isdir === 'true') {
          const p = h.dataset.path || '/';
          if (h.dataset.side === 'LEFT') this.leftCwd = p;
          else this.rightCwd = p;
          void this.refreshBothPanes(container);
        }
      });
    });

    container.querySelectorAll('.act-sftp-edit').forEach(btn => {
      btn.addEventListener('click', async () => {
        const h = btn as HTMLElement;
        const nodeId = h.dataset.node || 'ubuntu-pc';
        const filePath = h.dataset.path || '';
        const res = await this.apiFetch<{ path: string; content: string; mode: string }>(
          `/api/v1/files/read?nodeId=${encodeURIComponent(nodeId)}&path=${encodeURIComponent(filePath)}`
        );
        this.editorState = {
          nodeId,
          path: res.path,
          content: res.content,
          originalContent: res.content,
          mode: res.mode,
          lang: this.detectLanguage(res.path)
        };
        this.render(container);
      });
    });

    const textarea = container.querySelector('#nx-editor-textarea') as HTMLTextAreaElement | null;
    const gutter = container.querySelector('#nx-editor-gutter') as HTMLElement | null;
    if (textarea && gutter) {
      textarea.addEventListener('scroll', () => {
        gutter.scrollTop = textarea.scrollTop;
      });
      textarea.addEventListener('input', () => {
        if (this.editorState) {
          this.editorState.content = textarea.value;
          const lines = Math.max(1, textarea.value.split('\n').length);
          gutter.textContent = Array.from({ length: lines }, (_, i) => i + 1).join('\n');
        }
      });
      textarea.addEventListener('keydown', e => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
          e.preventDefault();
          void saveEditorFile();
        }
      });
    }

    const saveEditorFile = async () => {
      if (!this.editorState) return;
      const content = textarea ? textarea.value : this.editorState.content;
      const res = await this.apiFetch<{ bytesWritten: number }>('/api/v1/files/write', {
        method: 'POST',
        body: JSON.stringify({
          nodeId: this.editorState.nodeId,
          path: this.editorState.path,
          content
        })
      });
      this.editorState.content = content;
      this.editorState.originalContent = content;
      this.onStatus(`Saved remote file ${this.editorState.path} (${res.bytesWritten} bytes)`);
      await this.refreshBothPanes(container);
    };

    container.querySelector('#nx-editor-save')?.addEventListener('click', () => {
      void saveEditorFile();
    });

    // Cross-pane file copy
    container.querySelectorAll('.act-sftp-copy').forEach(btn => {
      btn.addEventListener('click', async () => {
        const h = btn as HTMLElement;
        const side = h.dataset.side || 'LEFT';
        const srcPath = h.dataset.path || '';
        const name = h.dataset.name || 'file';
        const srcNode = side === 'LEFT' ? this.leftNodeId : this.rightNodeId;
        const dstNode = side === 'LEFT' ? this.rightNodeId : this.leftNodeId;
        const dstDir = side === 'LEFT' ? this.rightCwd : this.leftCwd;

        const fileData = await this.apiFetch<{ content: string }>(
          `/api/v1/files/read?nodeId=${encodeURIComponent(srcNode)}&path=${encodeURIComponent(srcPath)}`
        );
        const b64 = btoa(unescape(encodeURIComponent(fileData.content)));
        await this.enqueueAndRunUpload(srcNode, dstNode, dstDir, name, b64, container);
      });
    });

    // Chmod / Chown Modal
    container.querySelectorAll('.act-sftp-chmod-open').forEach(btn => {
      btn.addEventListener('click', () => {
        const h = btn as HTMLElement;
        this.chmodModal = {
          nodeId: h.dataset.node || 'ubuntu-pc',
          path: h.dataset.path || '',
          mode: h.dataset.mode || '0644',
          owner: h.dataset.owner || 'root',
          group: h.dataset.group || 'root'
        };
        this.render(container);
      });
    });

    container.querySelector('#nx-close-chmod')?.addEventListener('click', () => {
      this.chmodModal = null;
      this.render(container);
    });

    container.querySelector('#nx-apply-chmod')?.addEventListener('click', async () => {
      if (!this.chmodModal) return;
      const mode = (container.querySelector('#chmod-mode') as HTMLInputElement).value.trim();
      const owner = (container.querySelector('#chmod-owner') as HTMLInputElement).value.trim();
      const group = (container.querySelector('#chmod-group') as HTMLInputElement).value.trim();
      const res = await this.apiFetch<{ message: string }>('/api/v1/files/chmod', {
        method: 'POST',
        body: JSON.stringify({
          nodeId: this.chmodModal.nodeId,
          path: this.chmodModal.path,
          mode,
          owner,
          group
        })
      });
      this.chmodModal = null;
      this.onStatus(res.message);
      await this.refreshBothPanes(container);
    });

    // Delete file/folder
    container.querySelectorAll('.act-sftp-del').forEach(btn => {
      btn.addEventListener('click', async () => {
        const h = btn as HTMLElement;
        const nodeId = h.dataset.node || 'ubuntu-pc';
        const targetPath = h.dataset.path || '';
        await this.apiFetch('/api/v1/files/delete', {
          method: 'POST',
          body: JSON.stringify({ nodeId, path: targetPath })
        });
        this.onStatus(`Deleted ${targetPath}`);
        await this.refreshBothPanes(container);
      });
    });

    // File input & Drag-and-Drop uploads
    container.querySelectorAll('.act-sftp-upload').forEach(inp => {
      inp.addEventListener('change', () => {
        const el = inp as HTMLInputElement;
        const side = el.dataset.side || 'LEFT';
        if (el.files) {
          this.handleBrowserFilesUpload(Array.from(el.files), side as 'LEFT' | 'RIGHT', container);
        }
      });
    });

    container.querySelectorAll('.act-dropzone').forEach(zone => {
      zone.addEventListener('dragover', e => {
        e.preventDefault();
        (zone as HTMLElement).style.borderColor = '#3B82F6';
      });
      zone.addEventListener('dragleave', () => {
        (zone as HTMLElement).style.borderColor = '#1E2430';
      });
      zone.addEventListener('drop', e => {
        e.preventDefault();
        (zone as HTMLElement).style.borderColor = '#1E2430';
        const de = e as DragEvent;
        const side = ((zone as HTMLElement).dataset.side || 'LEFT') as 'LEFT' | 'RIGHT';
        if (de.dataTransfer?.files && de.dataTransfer.files.length > 0) {
          this.handleBrowserFilesUpload(Array.from(de.dataTransfer.files), side, container);
        }
      });
    });

    container.querySelector('#nx-clear-queue')?.addEventListener('click', () => {
      this.queue = this.queue.filter(q => q.status !== 'COMPLETED');
      this.render(container);
    });

    container.querySelectorAll('.act-retry-tx').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = (btn as HTMLElement).dataset.id;
        const item = this.queue.find(q => q.id === id);
        if (item && item.base64Payload) {
          await this.runQueueItem(item, container);
        }
      });
    });
  }

  private handleBrowserFilesUpload(files: File[], side: 'LEFT' | 'RIGHT', container: HTMLElement): void {
    const targetNode = side === 'LEFT' ? this.leftNodeId : this.rightNodeId;
    const targetDir = side === 'LEFT' ? this.leftCwd : this.rightCwd;

    for (const file of files) {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = String(reader.result || '');
        void this.enqueueAndRunUpload('browser-local', targetNode, targetDir, file.name, b64, container);
      };
      reader.readAsDataURL(file);
    }
  }

  private async enqueueAndRunUpload(
    sourceNode: string,
    targetNode: string,
    targetDir: string,
    fileName: string,
    base64Payload: string,
    container: HTMLElement
  ): Promise<void> {
    const item: TransferQueueItem = {
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      fileName,
      sourceNode,
      targetNode,
      targetDir,
      sizeBytes: Math.round((base64Payload.length * 3) / 4),
      status: 'QUEUED',
      base64Payload
    };
    this.queue.unshift(item);
    await this.runQueueItem(item, container);
  }

  private async runQueueItem(item: TransferQueueItem, container: HTMLElement): Promise<void> {
    try {
      item.status = 'TRANSFERRING';
      const res = await this.apiFetch<{ path: string; size: number }>('/api/v1/files/upload', {
        method: 'POST',
        body: JSON.stringify({
          nodeId: item.targetNode,
          targetDir: item.targetDir,
          fileName: item.fileName,
          base64Data: item.base64Payload || ''
        })
      });
      item.status = 'COMPLETED';
      item.sizeBytes = res.size;
      delete item.base64Payload;
      this.onStatus(`Transferred ${res.path} (${res.size} bytes)`);
      await this.refreshBothPanes(container);
    } catch (e) {
      item.status = 'ERROR';
      item.errorMsg = e instanceof Error ? e.message : 'Transfer error';
      this.render(container);
    }
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  private escapeHtml(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
